'use client';

import CategoryCard from '@/app/(webapp)/_components/category-card';
import ContributorCarousel from '@/app/(webapp)/_components/contributor-carousel';
import MediaCarousel from '@/app/(webapp)/_components/media-carousel';
import UsageCard from '@/app/(webapp)/_components/usage-card';
import { partners } from '@/app/data/partners';
import { producers } from '@/app/data/producers';
import { StaticAdditionalPrograms, StaticMainPrograms } from '@/app/data/staticsPrograms';
import AppButton from '@/components/ui/app-button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import Cta from '@/components/ui/cta';
import { IconChevron } from '@/components/ui/icons';
import { Skeleton } from '@/components/ui/skeleton';
import { useUserData } from '@/context/auth/user-data-context';
import type { Category, Identifier, Program } from '@/graphql/graphql';
import { ContentType } from '@/graphql/graphql';
import { fetchCategoriesList } from '@/lib/api-categories';
import { fetchProgramsList } from '@/lib/api-programs';
import { StaticProgram, StaticProgramsRail } from '@/types/staticProgram';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

type ProgramRail = Category & { programs: { data: Program[]; total: number } };
type ProgramsListResponse = { data: Program[]; total: number };

const renderSkeletons = () => {
    return (
        <div className="md:ml-18">
            <Skeleton className="bg-white/10 rounded h-8 w-64 mb-4" />
            <div className="hidden md:grid grid-cols-5 gap-4">
                {Array.from({ length: 5 }).map(() => (
                    <Skeleton key={uuidv4()} className="bg-white/10 rounded-[6px] aspect-3/4 w-full flex flex-col" />
                ))}
            </div>

            <div className="grid md:hidden grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map(() => (
                    <Skeleton key={uuidv4()} className="bg-white/10 rounded-[6px] aspect-3/4 w-full flex flex-col" />
                ))}
            </div>
        </div>
    );
};

const renderCategoriesSkeleton = (title: string) => {
    return (
        <div className="mx-5 md:mx-18 pt-5 pb-10 md:pt-8 md:pb-20 gap-16">
            <section className="flex flex-col">
                <h2 className="font-bold text-xl md:text-2xl mb-5">{title}</h2>
                <Carousel
                    opts={{
                        align: 'start',
                        loop: true,
                        slidesToScroll: 1,
                    }}
                >
                    <CarouselContent>
                        {Array.from({ length: 6 }).map(() => (
                            <CarouselItem
                                key={uuidv4()}
                                className={'basis-[calc(100%/2)] md:basis-[calc(100%/4)] lg:basis-[calc(100%/6)]'}
                            >
                                <Skeleton className="bg-white/10 rounded-[16px] h-[80px] md:h-[129px] md:flex-1 mb-0 md:mb-5 mr-[10px] md:mr-0" />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
            </section>
        </div>
    );
};

export default function Home() {
    const [programRails, setProgramRails] = useState<ProgramRail[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [latestPrograms, setLatestPrograms] = useState<ProgramsListResponse>({ data: [], total: 0 });

    const [categoriesLoaded, setCategoriesLoaded] = useState(false);
    const [latestProgramsLoaded, setLatestProgramsLoaded] = useState(false);
    const [programRailsLoaded, setProgramRailsLoaded] = useState(false);

    const locale = useLocale().toLowerCase();

    const { isSeller, isBuyer, user, loading } = useUserData();
    const t = useTranslations();

    useEffect(() => {
        if (!loading) {
            const fetchData = async () => {
                const excludedGenres = process.env.NEXT_PUBLIC_EXCLUDED_GENRE_IDS
                    ? process.env.NEXT_PUBLIC_EXCLUDED_GENRE_IDS.replace(/\s+/g, '')
                          .split(',')
                          .map((id) => id.trim())
                    : [];

                // Get all categories
                let categoriesRes: { data: Category[]; total: number } = { data: [], total: 0 };
                try {
                    categoriesRes = await fetchCategoriesList({ contentType: ContentType.Program });
                    setCategories(categoriesRes.data);
                    setCategoriesLoaded(true);
                } catch {
                    setCategories([]);
                }

                // Get latests programs for the "New" rail
                try {
                    const lProgramsRes = await fetchProgramsList({
                        pageSize: 15,
                        excludedGenres: excludedGenres,
                        sortOrder: 'desc',
                        sortBy: 'createdAt',
                        skipAuth: isSeller,
                    });

                    setLatestPrograms(lProgramsRes);
                    setLatestProgramsLoaded(true);
                } catch {
                    setLatestPrograms({ data: [], total: 0 });
                    setLatestProgramsLoaded(false);
                }

                // Get programs for each category rails
                try {
                    // Temporary filter to keep only Documentary & TV report - TO BE REMOVED
                    const filteredCategories = categoriesRes.data.filter(
                        (category: Category) => category.id === 'e33ca248-5480-43cb-b434-8322b51e6b9b',
                    );
                    const rails: ProgramRail[] = [];
                    for (const category of filteredCategories) {
                        const programs = await fetchProgramsList({
                            pageSize: 15,
                            excludedGenres: excludedGenres,
                            filters: { categoryIds: [category.id] },
                            sortBy: 'createdAt',
                            skipAuth: isSeller,
                            random: true,
                        });

                        rails.push({ ...category, programs });
                        setProgramRails([...rails]);
                        setProgramRailsLoaded(true);
                    }
                } catch {
                    setProgramRails([]);
                    setProgramRailsLoaded(false);
                }
            };

            void fetchData();
        }
    }, [locale, loading, isSeller]);

    const _renderLatestProgramsSection = () => {
        if (latestPrograms.total < 6) {
            return <></>;
        }
        return !latestProgramsLoaded ? (
            renderSkeletons()
        ) : (
            <MediaCarousel
                autoplay={true}
                mediaList={latestPrograms.data}
                title={t.rich('HomePage.sections.newProgramsTitle')}
                viewAllSrc="/programs"
                viewAllLabel={t('HomePage.sections.viewAllLabel')}
            />
        );
    };

    return (
        <main className="font-[family-name:var(--font-geist-sans)]">
            {/* <div className="absolute top-0 left-0 z-[-2] h-[100vh] w-[100%] bg-[url('/images/shade.png')] bg-center bg-cover bg-no-repeat" /> */}
            <div className="absolute top-0 left-0 z-[-1] h-[100vh] w-[100%]" />
            {/* Section Hero */}
            <div className="items-center py-8 sm:pb-10 gap-16 sm:py-20">
                <section className="flex flex-col gap-[32px] px-4">
                    <h1 className="gobold-title flex items-center justify-center uppercase text-white text-4xl md:text-6xl font-extrabold text-center px-4">
                        {t('HomePage.hero.titleRich.label1')}
                        <IconChevron className="w-[auto] h-[1em] mx-[10px]" />
                        {t('HomePage.hero.titleRich.label2')}
                    </h1>
                    <div className="text-xl md:text-2xl text-white text-center mx-auto max-w-[700px]">
                        {t('HomePage.hero.subtitle')}
                    </div>

                    <div className="flex flex-col md:flex-row justify-center">
                        {loading ? (
                            <>
                                <Skeleton className="mb-5 md:mr-7 md:mb-0 h-[58px] w-full md:w-[270px] rounded-[12px] bg-white/10" />
                                <Skeleton className="mb-5 md:mb-0 h-[58px] w-full md:w-[270px] rounded-[12px] bg-white/10" />
                            </>
                        ) : !user ? (
                            <>
                                <AppButton
                                    customClassName="mb-5 md:mr-7 md:mb-0 w-full md:w-[auto]"
                                    innerText={t('HomePage.hero.requestDemo')}
                                    btnBgColor="#DD06AE"
                                    externalUrl="mailto:contact@bunqer.tech"
                                />
                                <AppButton
                                    customClassName="mb-5 md:mb-0 w-full md:w-[auto]"
                                    innerText={t('HomePage.hero.selectPrograms')}
                                    borderWidth="1"
                                    href="/programs"
                                />
                            </>
                        ) : (
                            <>
                                <AppButton
                                    customClassName="mb-5 md:mr-7 md:mb-0 w-full md:w-[auto]"
                                    innerText={t('HomePage.hero.browseAllPrograms')}
                                    borderWidth="1"
                                    href="/programs"
                                />
                                <AppButton
                                    customClassName="mb-5 md:mb-0 w-full md:w-[auto]"
                                    innerText={t('HomePage.hero.browseAllFormats')}
                                    borderWidth="1"
                                    href="/formats"
                                />
                            </>
                        )}
                    </div>

                    {!loading && !isBuyer ? (
                        <div className="flex flex-col md:flex-row max-w-[1015px] items-stretch mt-5 md-mt-10 mx-auto">
                            <UsageCard
                                className="flex-1 my-5 md:my-0"
                                title={t('HomePage.process.step1.title')}
                                text={t.rich('HomePage.process.step1.description', {
                                    highlight: (c: React.ReactNode) => <b>{c}</b>,
                                })}
                            />
                            <IconChevron className="w-[48px] h-[48px] self-center mx-[18px] rotate-90 md:rotate-0" />
                            <UsageCard
                                className="flex-1 my-5 md:my-0"
                                title={t('HomePage.process.step2.title')}
                                text={t.rich('HomePage.process.step2.description', {
                                    highlight: (c: React.ReactNode) => <b>{c}</b>,
                                })}
                            />
                            <IconChevron className="w-[48px] h-[48px] self-center mx-[18px] rotate-90 md:rotate-0" />
                            <UsageCard
                                className="flex-1 my-5 md:my-0"
                                title={t('HomePage.process.step3.title')}
                                text={t.rich('HomePage.process.step3.description', {
                                    highlight: (c: React.ReactNode) => <b>{c}</b>,
                                })}
                            />
                        </div>
                    ) : undefined}
                </section>
            </div>

            {!isBuyer && !loading ? (
                <section className="flex flex-col items-center justify-center gap-[32px] mb-25">
                    <ContributorCarousel
                        title={t('HomePage.KeyPartners.title')}
                        contributorList={partners}
                        minQty={3}
                        customSpacing="30px"
                    />
                </section>
            ) : undefined}

            {/* Categories */}
            {!categoriesLoaded ? (
                renderCategoriesSkeleton(t('HomePage.sections.browseOfferTitle'))
            ) : (
                <div className="mx-5 md:mx-18 pt-5 pb-10 md:pt-8 md:pb-20 gap-16">
                    <section className="flex flex-col">
                        <h2 className="font-bold text-xl md:text-2xl mb-5">
                            {t('HomePage.sections.browseOfferTitle')}
                        </h2>

                        <Carousel
                            opts={{
                                align: 'start',
                                loop: true,
                                slidesToScroll: 1,
                            }}
                        >
                            <CarouselContent>
                                {categories.map((category) => {
                                    return (
                                        <CarouselItem
                                            key={category.id}
                                            className={
                                                'basis-[calc(100%/2)] md:basis-[calc(100%/4)] lg:basis-[calc(100%/6)]'
                                            }
                                        >
                                            <CategoryCard
                                                href={{
                                                    pathname: '/programs',
                                                    query: { category: category.id },
                                                }}
                                                className="md:flex-1 mb-0 md:mb-5 mr-[10px] md:mr-0"
                                                name={category.title}
                                                imageSrc={category.image}
                                            />
                                        </CarouselItem>
                                    );
                                })}
                                <CarouselItem
                                    key={'formats-category-carousel-item'}
                                    className={'basis-[calc(100%/2)] md:basis-[calc(100%/4)] lg:basis-[calc(100%/6)]'}
                                >
                                    <CategoryCard
                                        href={{
                                            pathname: '/formats',
                                        }}
                                        className="md:flex-1 mb-0 md:mb-5 mr-[10px] md:mr-0"
                                        name={'Formats'}
                                        imageSrc={'formats'}
                                    />
                                </CarouselItem>
                            </CarouselContent>
                            <div className="hidden md:block absolute left-0 top-0 h-[100%] w-[95px]">
                                <CarouselPrevious
                                    variant="ghost"
                                    className="cursor-pointer hover:bg-transparent disabled:opacity-0"
                                />
                            </div>
                            <div className="hidden md:block absolute right-0 top-0 h-[100%] w-[95px]">
                                <CarouselNext
                                    variant="ghost"
                                    className="cursor-pointer hover:bg-transparent disabled:opacity-0"
                                />
                            </div>
                        </Carousel>
                    </section>
                </div>
            )}

            <div className="flex flex-col gap-[15px] md:gap-[72px] mb-[15px] md:mb-[72px]">
                {/*renderLatestProgramsSection()*/}
                {StaticMainPrograms.length > 0
                    ? StaticMainPrograms.map((rail: StaticProgramsRail) => {
                          const programs: Program[] = rail.programs.map((p: StaticProgram) => {
                              return {
                                  ...p,
                                  category: {
                                      id: p.category.identifier.id,
                                      title: p.category.titles[locale] || '',
                                      image: '',
                                  },
                              } as Program;
                          });
                          return (
                              <MediaCarousel
                                  key={rail.id}
                                  mediaList={programs}
                                  title={rail.titles[locale]}
                                  viewAllSrc={{
                                      pathname: '/programs',
                                      ...(rail.categoryId?.trim() || rail.genreId?.trim()
                                          ? {
                                                query: {
                                                    ...(rail.categoryId?.trim() ? { category: rail.categoryId } : {}),
                                                    ...(rail.genreId?.trim() ? { gens: rail.genreId } : {}),
                                                },
                                            }
                                          : {}),
                                  }}
                                  viewAllLabel={t('HomePage.sections.viewAllLabel')}
                              />
                          );
                      })
                    : undefined}
            </div>

            {/* Program Rails */}
            <div className="flex flex-col gap-[15px] md:gap-[72px] mb-[50px] md:mb-[110px]">
                {programRailsLoaded
                    ? programRails.map((rail) => {
                          if (rail.programs.total > 6) {
                              return (
                                  <MediaCarousel
                                      key={rail.id}
                                      mediaList={rail.programs.data}
                                      title={rail.catchPhrase}
                                      viewAllSrc={{
                                          pathname: '/programs',
                                          query: { category: rail.id },
                                      }}
                                      viewAllLabel={t('HomePage.sections.viewAllLabel')}
                                  />
                              );
                          }
                      })
                    : null}

                {StaticAdditionalPrograms.length > 0
                    ? StaticAdditionalPrograms.map((rail: StaticProgramsRail) => {
                          const programs: Program[] = rail.programs.map((p: StaticProgram) => {
                              return {
                                  ...p,
                                  category: {
                                      id: p.category.identifier.id,
                                      title: p.category.titles[locale] || '',
                                      image: '',
                                  },
                              } as Program;
                          });
                          return (
                              <MediaCarousel
                                  key={rail.id}
                                  mediaList={programs}
                                  title={rail.titles[locale]}
                                  viewAllSrc={{
                                      pathname: '/programs',
                                      ...(rail.categoryId?.trim() || rail.genreId?.trim()
                                          ? {
                                                query: {
                                                    ...(rail.categoryId?.trim() ? { category: rail.categoryId } : {}),
                                                    ...(rail.genreId?.trim() ? { gens: rail.genreId } : {}),
                                                },
                                            }
                                          : {}),
                                  }}
                                  viewAllLabel={t('HomePage.sections.viewAllLabel')}
                              />
                          );
                      })
                    : undefined}

                {!user && programRailsLoaded ? (
                    <Cta
                        title={t('HomePage.cta.title')}
                        ctaLabel={t('HomePage.cta.buttonLabel')}
                        externalUrl="mailto:contact@bunqer.tech"
                    />
                ) : undefined}
            </div>

            {/* Contributors */}
            {!loading ? (
                <div className="items-center justify-items-center gap-16 pb-15 md:pb-20 bg-[rgba(255,255,255,0.10)] overflow-hidden">
                    <section className="flex flex-col items-center justify-center gap-[32px]">
                        <ContributorCarousel
                            title={t('HomePage.Sellers.title')}
                            contributorList={producers}
                            minQty={10}
                        />
                    </section>
                </div>
            ) : undefined}
        </main>
    );
}
